export const ROLE_PERMISSIONS = {
  owner: ["*"],
  admin: ["*"],
  manager: [
    "dashboard.view",
    "catalog.view",
    "catalog.manage",
    "orders.view",
    "orders.manage",
    "integrations.view",
    "integrations.manage",
    "inventory.view",
    "inventory.manage",
    "reports.view",
    "customers.view",
    "customers.manage",
    "settings.view",
    "logs.view",
  ],
  operator: [
    "dashboard.view",
    "catalog.view",
    "orders.view",
    "orders.manage",
    "integrations.view",
    "inventory.view",
    "inventory.manage",
    "customers.view",
  ],
  accountant: [
    "dashboard.view",
    "orders.view",
    "reports.view",
    "billing.view",
    "billing.manage",
    "customers.view",
  ],
  support: ["dashboard.view", "orders.view", "customers.view", "logs.view"],
  viewer: ["dashboard.view", "catalog.view", "orders.view", "integrations.view", "reports.view", "customers.view"],
};

const RESERVED_ROOT_SEGMENTS = new Set([
  "",
  "giris",
  "kayit",
  "sifremi-unuttum",
  "auth",
  "super",
  "super-giris",
  "panel",
  "api",
  "assets",
  "static",
  "onboarding",
]);

const PATH_RULES = [
  { prefix: "/panel/entegrasyonlar", requiredAny: ["integrations.view", "integrations.manage"] },
  { prefix: "/panel/urunler", requiredAny: ["catalog.view", "catalog.manage"] },
  { prefix: "/panel/stok", requiredAny: ["inventory.view", "inventory.manage"] },
  { prefix: "/panel/urun-stok", requiredAny: ["inventory.view", "inventory.manage"] },
  { prefix: "/panel/urun-fiyat", requiredAny: ["inventory.view", "inventory.manage"] },
  { prefix: "/panel/operasyon", requiredAny: ["inventory.view", "inventory.manage"] },
  { prefix: "/panel/satislar/faturalar", requiredAny: ["billing.view", "billing.manage"] },
  { prefix: "/panel/giderler", requiredAny: ["billing.view", "billing.manage"] },
  { prefix: "/panel/raporlar", requiredAny: ["reports.view"] },
  { prefix: "/panel/finans", requiredAny: ["billing.view", "billing.manage", "reports.view"] },
  { prefix: "/panel/musteriler", requiredAny: ["customers.view", "customers.manage"] },
  { prefix: "/panel/ayarlar", requiredAny: ["settings.view", "settings.manage"] },
  { prefix: "/panel/hesabim", requiredAny: ["dashboard.view"] },
  { prefix: "/panel/loglar", requiredAny: ["logs.view"] },
  { prefix: "/panel", requiredAny: ["dashboard.view"] },
];

function normalizeRole(rawRole) {
  if (!rawRole || typeof rawRole !== "string") return "owner";
  const role = rawRole.toLowerCase();
  if (role === "super_admin" || role === "superadmin") return "owner";
  if (ROLE_PERMISSIONS[role]) return role;
  return "owner";
}

export function normalizeUserRole(user) {
  return normalizeRole(user?.role);
}

export function permissionSet(user) {
  const role = normalizeUserRole(user);
  const fromRole = ROLE_PERMISSIONS[role] || [];
  const explicit = Array.isArray(user?.permissions) ? user.permissions.filter((x) => typeof x === "string") : [];
  return Array.from(new Set([...fromRole, ...explicit]));
}

export function hasAnyPermission(granted, requiredAny) {
  if (!requiredAny || requiredAny.length === 0) return true;
  if (!Array.isArray(granted)) return false;
  if (granted.includes("*")) return true;
  return requiredAny.some((key) => granted.includes(key));
}

export function canAccessPath(user, pathname) {
  const granted = permissionSet(user);
  const panelPath = toPanelPath(pathname);
  const rule = PATH_RULES.find((r) => panelPath === r.prefix || panelPath.startsWith(r.prefix + "/"));
  if (!rule) return true;
  return hasAnyPermission(granted, rule.requiredAny);
}

export function getTenantSlug(user) {
  const candidates = [
    user?.panel_slug,
    user?.tenant_slug,
    user?.company_short_code,
    user?.company_uid,
    user?.selected_company_id,
    user?.company_id,
    user?.firm_id,
  ];
  for (const candidate of candidates) {
    const value = String(candidate || "").trim();
    if (!value) continue;
    if (RESERVED_ROOT_SEGMENTS.has(value)) continue;
    return value;
  }
  return "";
}

export function getPanelBasePath(user) {
  const slug = getTenantSlug(user);
  return slug ? `/${slug}` : "/panel";
}

export function toPanelPath(pathname) {
  const raw = String(pathname || "").trim() || "/";
  const onlyPath = raw.split("?")[0].split("#")[0] || "/";
  if (onlyPath === "/panel" || onlyPath.startsWith("/panel/")) return onlyPath;
  const match = onlyPath.match(/^\/([^/]+)(\/.*)?$/);
  if (!match) return onlyPath;
  const first = String(match[1] || "");
  if (RESERVED_ROOT_SEGMENTS.has(first)) return onlyPath;
  const rest = match[2] || "";
  return `/panel${rest}`;
}

export function resolvePanelBase(pathname, user) {
  const preferred = getPanelBasePath(user);
  if (preferred && preferred !== "/panel") return preferred;
  const raw = String(pathname || "").trim() || "/";
  const onlyPath = raw.split("?")[0].split("#")[0] || "/";
  if (onlyPath === "/panel" || onlyPath.startsWith("/panel/")) return "/panel";
  const match = onlyPath.match(/^\/([^/]+)(\/.*)?$/);
  const first = String(match?.[1] || "");
  if (first && !RESERVED_ROOT_SEGMENTS.has(first)) {
    return `/${first}`;
  }
  return getPanelBasePath(user);
}

export function withPanelBase(path, user, currentPathname) {
  const raw = String(path || "").trim() || "/panel";
  const panelPath = raw.startsWith("/panel") ? raw : toPanelPath(raw);
  const suffix = panelPath === "/panel" ? "" : panelPath.slice("/panel".length);
  const base = resolvePanelBase(currentPathname, user);
  return `${base}${suffix}`;
}

export function isPanelRoutePath(pathname) {
  const panelPath = toPanelPath(pathname);
  return panelPath === "/panel" || panelPath.startsWith("/panel/");
}

export function getDefaultPanelPath(user) {
  const role = normalizeUserRole(user);
  const base = getPanelBasePath(user);
  if (role === "accountant") return `${base}/satislar/faturalar`;
  if (role === "operator") return `${base}/siparisler`;
  if (role === "support") return `${base}/musteriler`;
  if (role === "viewer") return `${base}/raporlar`;
  return base;
}
